import React, { useState, useRef, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import TrackingScanService from './TrackingScanService';
import useShipDate from '../../setting/shipDate/useShipDate';
import { formatDate } from '../../../utils/dateUtils';
import { speakThai as speakText } from '../../../utils/speakThai';
import GetCustomerProfileService from '../../../services/GetCustomerProfileService';
import WeeklyShipService from '../../weeklyShip/WeeklyShipService';
import HeldItemsModal, { FindOrderModal } from './HeldItemsModal';

// alphanumeric 10–15 chars = valid TrackingJP format
const isValidTrackingFormat = (value) => /^[A-Za-z0-9]{10,15}$/.test(value);

// min 0.1 kg, step 0.05, always round up
const applyWeightRule = (raw) => {
  if (raw == null || raw <= 0) return null;
  const STEP = 0.05;
  const MIN  = 0.1;
  const clamped = Math.max(raw, MIN);
  return parseFloat((Math.ceil(clamped / STEP) * STEP).toFixed(2));
};

const TrackingLookup = () => {
  const [trackingInput, setTrackingInput] = useState('');
  const [result, setResult]               = useState(null);
  const [isLoading, setIsLoading]         = useState(false);
  const [notFound, setNotFound]           = useState(false);
  const [history, setHistory]             = useState([]);
  const [shipDateId, setShipDateId]       = useState('');
  const [boxNo, setBoxNo]                 = useState(1);
  const { shipDates, getShipDates }       = useShipDate();
  const [findModalOpen, setFindModalOpen] = useState(false);
  const [heldModalOpen, setHeldModalOpen] = useState(false);
  const [bypassDuplicate, setBypassDuplicate] = useState(false);
  const [bypassNotFound, setBypassNotFound] = useState(false);
  const [scanOnly, setScanOnly] = useState(false);
  // SMILE-only mode: only accept dealer QR reads (SMILE…), ignore anything else so a stray
  // scan of the tracking barcode beside the QR can't fall into the legacy flow. Persisted.
  const [smileOnly, setSmileOnly] = useState(() => localStorage.getItem('dealerSmileOnly') === '1');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [chooser, setChooser] = useState({ open: false, boxNo: null, shipDate: null, candidates: [] });
  const [profiles, setProfiles] = useState([]);
  const [profilesReady, setProfilesReady] = useState(false);

  const [scaleStatus, setScaleStatus] = useState('disconnected');
  const [liveWeight, setLiveWeight]   = useState(null);
  const latestWeightRef = useRef(null);
  const portRef         = useRef(null);
  const scaleReaderRef  = useRef(null);

  const inputRef = useRef(null);

  useEffect(() => { localStorage.setItem('dealerSmileOnly', smileOnly ? '1' : '0'); }, [smileOnly]);

  useEffect(() => {
    inputRef.current?.focus();
    getShipDates();
    TrackingScanService.getScanLogs(100, 'tracking_lookup')
      .then((res) => setHistory(res.data))
      .catch(() => {});
    // Fetch customer profiles once here and pass to the Find Order modal, so the
    // modal doesn't refetch every time it reopens.
    GetCustomerProfileService.getCustomerProfiles(undefined)
      .then((r) => { setProfiles(r.data); setProfilesReady(true); })
      .catch(() => setProfilesReady(true));
  }, []);

  // resolve the ISO date string from selected ship date ID
  const resolveShipDateStr = () => {
    const found = shipDates.find((d) => String(d.id) === String(shipDateId));
    if (!found?.shipDate) return '';
    return new Date(found.shipDate).toISOString().slice(0, 10);
  };


  // ── Scale serial ──────────────────────────────────────────────────────────

  const connectScale = async () => {
    if (!navigator.serial) {
      toast.error('เบราว์เซอร์นี้ไม่รองรับ Web Serial — ใช้ Chrome หรือ Edge');
      return;
    }
    try {
      const port = await navigator.serial.requestPort();
      await port.open({ baudRate: 9600, dataBits: 8, stopBits: 1, parity: 'none' });
      portRef.current = port;
      setScaleStatus('connected');
      scaleReadLoop(port);
    } catch (err) {
      if (err.name !== 'NotFoundError') toast.error(`เชื่อมต่อตาชั่งไม่ได้: ${err.message}`);
    }
  };

  const disconnectScale = async () => {
    try {
      await scaleReaderRef.current?.cancel();
      await portRef.current?.close();
    } catch {}
    portRef.current = null;
    scaleReaderRef.current = null;
    setScaleStatus('disconnected');
    setLiveWeight(null);
    latestWeightRef.current = null;
  };

  const scaleReadLoop = useCallback(async (port) => {
    const decoder = new TextDecoder();
    let buf = '';
    try {
      const reader = port.readable.getReader();
      scaleReaderRef.current = reader;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split(/\r?\n/);
        buf = lines.pop();
        for (const line of lines) {
          const w = parseFloat(line.trim());
          if (!isNaN(w)) { latestWeightRef.current = w; setLiveWeight(w); }
        }
      }
      reader.releaseLock();
    } catch (err) {
      if (err.name !== 'AbortError') toast.error(`ตาชั่งหลุด: ${err.message}`);
    }
    setScaleStatus('disconnected');
    setLiveWeight(null);
    latestWeightRef.current = null;
  }, []);

  // ── Generate label (no barcode) ───────────────────────────────────────────

  // Reserve a WeeklyShip box for this print — the backend assigns the boxNo (MAX+1
  // for the round) — then send to the local print server using the boxNo it returned.
  const arriveThenPrint = async ({ smileNameInput, billableWeight, trackingJp, orderId, scanLogId }) => {
    let resData;
    try {
      const res = await WeeklyShipService.arriveAirWeeklyShip({
        shipDateId,
        weight: billableWeight,
        trackingJp: trackingJp || null,
        smileNameInput: smileNameInput || null,
        orderId: orderId || null,
        scanLogId: scanLogId || null,
      });
      resData = res.data; // { weeklyShip, scanLog }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'สร้างกล่องไม่สำเร็จ');
      return;
    }
    const assignedBoxNo = resData.weeklyShip.boxNo;
    setBoxNo(assignedBoxNo + 1); // display only; the backend owns the real counter

    // ODA per_kg boxes auto-match to their order at scan (skip the manual match screen).
    if (resData.autoMatched) {
      if (resData.scanLog) resData.scanLog = { ...resData.scanLog, autoMatched: true };
      toast.success('จับคู่ออเดอร์อัตโนมัติแล้ว ✓');
    }

    try {
      const r = await fetch('http://localhost:5200/print', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          smileNameInput: smileNameInput || '',
          billableWeight,
          shipDate:   resolveShipDateStr(),
          trackingJp: trackingJp || '',
          orderId:    orderId || '',
          boxNo:      assignedBoxNo,
        }),
      });
      if (!r.ok) toast.error('พิมพ์ label ไม่สำเร็จ');
    } catch {
      toast.error('พิมพ์ label ไม่ได้ — เปิด print server ก่อน');
    }

    return resData;
  };

  // Hold an unidentified parcel: park it with no shipDate, tagged A1/B2 by intake batch
  // (the selected round), keep the scanned weight, and print the HOLD label. It's promoted
  // into a real round later from the Held Items list.
  const holdThenPrint = async ({ trackingJp, billableWeight, scanLogId }) => {
    let resData;
    try {
      const res = await WeeklyShipService.holdAirWeeklyShip({
        originShipDateId: shipDateId,
        weight: billableWeight,
        trackingJp: trackingJp || null,
        scanLogId: scanLogId || null,
      });
      resData = res.data; // { weeklyShip, scanLog, holdTag }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'พักของไม่สำเร็จ');
      return;
    }
    const { holdTag } = resData;

    try {
      const r = await fetch('http://localhost:5200/print-hold', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          holdTag,
          billableWeight,
          trackingJp: trackingJp || '',
        }),
      });
      if (!r.ok) toast.error('พิมพ์ label พักไม่สำเร็จ');
    } catch {
      toast.error('พิมพ์ label พักไม่ได้ — เปิด print server ก่อน');
    }
    toast.info(`พักไว้: ${holdTag}`);
    speakText('พักไว้');

    return resData;
  };

  // Void a box created by mistake (double scan / wrong press): removes the box and
  // its history row.
  const handleCancelBox = async (row) => {
    if (!row.weeklyShipUuid) return;
    if (!window.confirm(`ยกเลิกกล่อง ${row.matchedBoxNo || ''} ?`)) return;
    try {
      await WeeklyShipService.cancelAirWeeklyShip(row.weeklyShipUuid);
      setHistory((prev) => prev.filter((h) => h.id !== row.id));
      toast.success('ยกเลิกกล่องแล้ว');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'ยกเลิกไม่สำเร็จ');
    }
  };

  // Print a box for the given order (or null = anonymous). Prepends to history and
  // clears the selected order afterward so the next print can't reuse it by mistake.
  const printForOrder = async (order) => {
    if (!shipDateId) { toast.warning('กรุณาเลือกรอบเรือก่อน'); return; }
    const billableWeight = applyWeightRule(latestWeightRef.current);
    const result = await arriveThenPrint({
      smileNameInput: order?.smileNameInput || '',
      billableWeight,
      trackingJp: order?.trackingJp || '',
      orderId: order?.id || '',
    });
    if (result?.scanLog) setHistory((prev) => [result.scanLog, ...prev]);
    setSelectedOrder(null);
  };

  // No order selected = owner unknown → park it ON-HOLD (no round, A-tag) instead of an
  // anonymous box: an unknown parcel shouldn't take a boxNo in a round it may miss. With
  // an order selected it's a normal box in the round, same as before.
  const handleGenerate = async () => {
    if (selectedOrder) { printForOrder(selectedOrder); return; }
    if (!shipDateId) { toast.warning('กรุณาเลือกรอบเรือก่อน'); return; }
    const billableWeight = applyWeightRule(latestWeightRef.current);
    const result = await holdThenPrint({ trackingJp: null, billableWeight, scanLogId: null });
    if (result?.scanLog) setHistory((prev) => [result.scanLog, ...prev]);
  };

  // ── Scan ─────────────────────────────────────────────────────────────────

  // Dealer (D2U) QR: SMILE{boxNo}/{shipDate}. The box already exists from the import, so
  // we only resolve it and print its label (customer name if matched, HOLD label if held)
  // — never creating a box the way the legacy tracking scan does.
  const isDealerQR = (v) => /^SMILE0*\d+\/\d{4}-\d{2}-\d{2}$/i.test(v);

  // Apply a resolved dealer-scan result (matched / held / not-arrived): show + speak + print +
  // log. Shared by a direct single-box scan and the chooser's resolve.
  const applyDealerScanResult = async (data) => {
    if (data.scanLog) setHistory((prev) => [data.scanLog, ...prev]);

    // Duplicate-parcel guard: this box was already scanned (maybe 1 boxNo in Excel but 2 actual
    // parcels). Show the info but DON'T print unless "พิมพ์ซ้ำได้" is ticked.
    if ((data.status === 'matched' || data.status === 'held') && data.alreadyScanned && !bypassDuplicate) {
      const ts = data.firstScannedAt ? new Date(data.firstScannedAt).toLocaleString('th-TH') : '';
      if (data.status === 'matched') {
        setResult({ contact: data.contact, shop: data.shop, smileNameInput: data.smileNameInput, smileName: data.smileName, amount: data.amount, qty: data.qty, url: data.url, trackingJp: data.trackingJp, billableWeight: data.weight });
      } else {
        setResult({ smileNameInput: `พักไว้: ${data.holdTag}`, trackingJp: data.trackingJp, billableWeight: data.weight });
      }
      speakText('สแกนซ้ำ');
      toast.error(`🛑 กล่อง #${data.boxNo} สแกนไปแล้ว${ts ? ` (${ts})` : ''} — อาจเป็นพัสดุซ้ำ. ติ๊ก "พิมพ์ซ้ำได้" เพื่อพิมพ์`);
      return;
    }
    if ((data.status === 'matched' || data.status === 'held') && data.alreadyScanned) {
      toast.warning('สแกนซ้ำ — พิมพ์ซ้ำเปิดอยู่');
    }

    if (data.status === 'matched') {
      const name = data.smileNameInput || data.smileName || '';
      setResult({ contact: data.contact, shop: data.shop, smileNameInput: data.smileNameInput, smileName: data.smileName, amount: data.amount, qty: data.qty, url: data.url, trackingJp: data.trackingJp, billableWeight: data.weight });
      if (name) speakText(name);
      try {
        const r = await fetch('http://localhost:5200/print', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ smileNameInput: name, billableWeight: data.weight, shipDate: data.shipDate, trackingJp: data.trackingJp || '', orderId: '', boxNo: data.boxNo }),
        });
        if (!r.ok) toast.error('พิมพ์ label ไม่สำเร็จ');
      } catch { toast.error('พิมพ์ label ไม่ได้ — เปิด print server ก่อน'); }
    } else if (data.status === 'held') {
      setResult({ smileNameInput: `พักไว้: ${data.holdTag}`, trackingJp: data.trackingJp, billableWeight: data.weight });
      speakText('พักไว้');
      try {
        const r = await fetch('http://localhost:5200/print-hold', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ holdTag: data.holdTag, billableWeight: data.weight, trackingJp: data.trackingJp || '' }),
        });
        if (!r.ok) toast.error('พิมพ์ label พักไม่สำเร็จ');
      } catch { toast.error('พิมพ์ label พักไม่ได้ — เปิด print server ก่อน'); }
      toast.info(`พักไว้: ${data.holdTag}`);
    } else if (data.status === 'not_arrived') {
      // Parked "not-arrived" box physically arrived: push it into the selected round (re-matches
      // + re-prices), then print the now-matched label. The QR's own date is the planned round.
      const { data: promoted } = await WeeklyShipService.dealerPromoteNotArrived(data.weeklyShipUuid, shipDateId);

      // Unknown owner → backend parked it back on HOLD instead of a nameless round box.
      // Print the HOLD label so it can be identified + promoted later.
      if (promoted.status === 'held') {
        setResult({ smileNameInput: `พักไว้: ${promoted.holdTag}`, trackingJp: data.trackingJp, billableWeight: data.weight });
        speakText('พักไว้');
        try {
          const r = await fetch('http://localhost:5200/print-hold', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ holdTag: promoted.holdTag, billableWeight: data.weight, trackingJp: data.trackingJp || '' }),
          });
          if (!r.ok) toast.error('พิมพ์ label พักไม่สำเร็จ');
        } catch { toast.error('พิมพ์ label พักไม่ได้ — เปิด print server ก่อน'); }
        toast.info(`ไม่ทราบเจ้าของ — พักไว้: ${promoted.holdTag}`);
        return;
      }

      const name = promoted.smileNameInput || promoted.smileName || '';
      const targetRound = shipDates.find((d) => String(d.id) === String(shipDateId));
      setResult({ contact: promoted.contact, smileNameInput: promoted.smileNameInput, smileName: promoted.smileName, trackingJp: data.trackingJp, billableWeight: data.weight });
      if (name) speakText(name);
      try {
        const r = await fetch('http://localhost:5200/print', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ smileNameInput: name, billableWeight: data.weight, shipDate: targetRound?.shipDate || '', trackingJp: data.trackingJp || '', orderId: '', boxNo: promoted.boxNo }),
        });
        if (!r.ok) toast.error('พิมพ์ label ไม่สำเร็จ');
      } catch { toast.error('พิมพ์ label ไม่ได้ — เปิด print server ก่อน'); }
      toast.success(`รับพัสดุตกรอบเข้ารอบแล้ว — กล่อง ${promoted.boxNo}`);
    }
  };

  const handleDealerScan = async (code) => {
    // Require a round to be selected before any dealer scan (matched / held / not-arrived).
    if (!shipDateId) {
      speakText('เลือกรอบเรือก่อน');
      toast.warning('เลือก "รอบเรือ" ก่อนสแกน');
      setTrackingInput('');
      setTimeout(() => inputRef.current?.focus(), 50);
      return;
    }
    setIsLoading(true);
    setResult(null);
    setNotFound(false);
    try {
      const { data } = await WeeklyShipService.dealerScanBox(code);
      if (data.status === 'choose') {
        // Duplicate dealerBoxNo → the QR hit 2+ twins; let the operator pick the right one.
        speakText('เลือกกล่อง');
        setChooser({ open: true, boxNo: data.boxNo, shipDate: data.shipDate, candidates: data.candidates || [] });
      } else {
        await applyDealerScanResult(data);
      }
    } catch (err) {
      const st = err?.response?.data?.status;
      if (st === 'not_found') { setNotFound(true); speakText('ไม่พบข้อมูล'); toast.warning(`ไม่พบกล่อง: ${code}`); }
      else toast.error('สแกนไม่สำเร็จ');
    } finally {
      setIsLoading(false);
      setTrackingInput('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  // Operator picked one twin from the chooser → resolve that specific box and apply the result.
  const handleChooserPick = async (candidate) => {
    const sd = chooser.shipDate;
    setChooser((c) => ({ ...c, open: false }));
    setIsLoading(true);
    try {
      const { data } = await WeeklyShipService.dealerScanResolve(candidate.weeklyShipUuid, sd);
      await applyDealerScanResult(data);
    } catch { toast.error('เลือกกล่องไม่สำเร็จ'); }
    finally { setIsLoading(false); setTimeout(() => inputRef.current?.focus(), 50); }
  };

  const handleKeyDown = async (e) => {
    if (e.key !== 'Enter') return;
    const value = trackingInput.trim();
    if (!value) return;

    // Dealer (D2U) QR → resolve the pre-imported box and print; skip the legacy flow.
    if (isDealerQR(value)) { await handleDealerScan(value); return; }

    // SMILE-only mode: a non-SMILE read (e.g. the tracking barcode beside the QR) is ignored
    // so it never falls into the legacy flow and creates a box.
    if (smileOnly) {
      speakText('อ่านเฉพาะ QR');
      toast.warning('โหมด SMILE เท่านั้น — ข้ามการอ่านที่ไม่ใช่ QR SMILE');
      setTrackingInput('');
      setTimeout(() => inputRef.current?.focus(), 50);
      return;
    }

    // invalid format (postcode, Thai chars, wrong length) → skip API, no log, no print
    if (!isValidTrackingFormat(value)) {
      setNotFound(true);
      speakText('ไม่พบข้อมูล');
      setTrackingInput('');
      setTimeout(() => inputRef.current?.focus(), 50);
      return;
    }

    setIsLoading(true);
    setResult(null);
    setNotFound(false);

    // scan only: look up and speak the name, but record nothing and print nothing
    if (scanOnly) {
      try {
        const { data } = await TrackingScanService.scan(value, true);
        const { order } = data;
        if (order) {
          setResult({ ...order, billableWeight: applyWeightRule(latestWeightRef.current) });
          const nameToSpeak = order.smileNameInput || order.smileName || '';
          if (nameToSpeak) speakText(nameToSpeak);
        } else {
          setNotFound(true);
          speakText('ไม่พบข้อมูล');
          toast.warning(`ไม่พบข้อมูลสำหรับ: ${value}`);
        }
      } catch {
        toast.error('เกิดข้อผิดพลาดในการค้นหา');
      } finally {
        setIsLoading(false);
        setTrackingInput('');
        setTimeout(() => inputRef.current?.focus(), 50);
      }
      return;
    }

    try {
      const { data } = await TrackingScanService.scan(value);
      const { order, scanLog, alreadyArrived, firstArrivedAt, onDealerBox } = data;

      // always add to history for valid-format barcodes (found or not found)
      if (scanLog) setHistory((prev) => [scanLog, ...prev]);

      // Dealer-aware: this tracking's order is already on a dealer box in a round. Creating a
      // legacy box here would duplicate it — warn to scan the SMILE QR instead (bypassable).
      if (onDealerBox && !bypassDuplicate) {
        toast.error(`🛑 เป็นกล่อง Dealer (กล่อง ${onDealerBox.dealerBoxNo}) อยู่ในรอบแล้ว — สแกน QR SMILE แทน (ติ๊ก "พิมพ์ซ้ำได้" เพื่อข้าม)`);
        speakText('เป็นกล่องดีลเลอร์');
        if (order) setResult({ ...order, billableWeight: applyWeightRule(latestWeightRef.current) });
        return;
      }

      if (alreadyArrived && !bypassDuplicate) {
        const ts = firstArrivedAt ? new Date(firstArrivedAt).toLocaleString('th-TH') : '';
        toast.error(`🛑 พัสดุซ้ำ — สแกนครั้งแรกเมื่อ ${ts} (ติ๊ก "พิมพ์ซ้ำได้" เพื่อข้าม)`);
        speakText('สแกนซ้ำ');
        if (order) setResult({ ...order, billableWeight: applyWeightRule(latestWeightRef.current) });
        return;
      }
      if (alreadyArrived) {
        const ts = firstArrivedAt ? new Date(firstArrivedAt).toLocaleString('th-TH') : '';
        toast.warning(`สแกนซ้ำ — สแกนครั้งแรกเมื่อ ${ts} (พิมพ์ซ้ำเปิดอยู่)`);
      }

      if (order) {
        const billableWeight = applyWeightRule(latestWeightRef.current);
        setResult({ ...order, billableWeight });
        const nameToSpeak = order.smileNameInput || order.smileName || '';
        if (nameToSpeak) speakText(nameToSpeak);
        if (!shipDateId) {
          toast.warning('กรุณาเลือกรอบเรือก่อนเพื่อพิมพ์ label');
        } else {
          const result = await arriveThenPrint({
            smileNameInput: order.smileNameInput,
            billableWeight,
            trackingJp: order.trackingJp,
            orderId: order.id,
            scanLogId: scanLog?.id,
          });
          // Reflect the box on the history row the lookup already added (shows boxNo + Cancel).
          if (result?.scanLog) {
            setHistory((prev) => prev.map((h) => (h.id === result.scanLog.id ? result.scanLog : h)));
          }
        }
      } else if (bypassNotFound && shipDateId) {
        // Not in DB, but print anyway: create the box carrying the scanned tracking,
        // so it can be matched later once the order's tracking is registered.
        setNotFound(true);
        const billableWeight = applyWeightRule(latestWeightRef.current);
        const result = await arriveThenPrint({
          smileNameInput: '',
          billableWeight,
          trackingJp: value,
          orderId: '',
          scanLogId: scanLog?.id,
        });
        if (result?.scanLog) {
          setHistory((prev) => prev.map((h) => (h.id === result.scanLog.id ? result.scanLog : h)));
        }
      } else if (shipDateId) {
        // Not in DB → auto-hold: valid-format tracking that isn't matched gets parked with
        // no shipDate, tagged by intake batch, and a HOLD label printed. The current batch
        // ships without waiting; we identify + promote it to a later round afterwards.
        setNotFound(true);
        const billableWeight = applyWeightRule(latestWeightRef.current);
        const holdResult = await holdThenPrint({
          trackingJp: value,
          billableWeight,
          scanLogId: scanLog?.id,
        });
        if (holdResult?.scanLog) {
          setHistory((prev) => prev.map((h) => (h.id === holdResult.scanLog.id ? holdResult.scanLog : h)));
        }
      } else {
        setNotFound(true);
        speakText('ไม่พบข้อมูล');
        toast.warning(`ไม่พบข้อมูล — เลือกรอบเรือก่อนเพื่อพักของ: ${value}`);
      }
    } catch {
      toast.error('เกิดข้อผิดพลาดในการค้นหา');
    } finally {
      setIsLoading(false);
      setTrackingInput('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  // ── Derived display values ────────────────────────────────────────────────

  const contact = result?.CustomerProfile?.contact || result?.contact || '-';
  const shop = result?.CustomerProfile?.shop || result?.shop || '-';
  const smileNameInput = result?.smileNameInput || '-';
  const smileName = result?.smileName || '-';
  const url = result?.url || '-';
  const amount        = result?.amount ?? '-';
  const qty           = result?.qty ?? '-';
  const billableWeight = result?.billableWeight;

  return (
    <>
      {/* ── Main UI ── */}
      <div className="max-w-2xl mx-auto p-6">

        {/* Header + scale status */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold mb-1">สแกน Tracking JP</h1>
            <p className="text-sm text-gray-500">สแกนบาร์โค้ดบนพัสดุ แล้วระบบจะอ่านชื่อลูกค้าออกเสียงและพิมพ์ label</p>
          </div>
          <ScaleStatus
            status={scaleStatus}
            weight={liveWeight}
            onConnect={connectScale}
            onDisconnect={disconnectScale}
          />
        </div>

        {/* Ship date selector — ODA J4410 only */}
        <div className="flex items-center gap-3 mb-5">
          <label className="text-lg text-gray-700 shrink-0">รอบเรือ</label>
          <select
            value={shipDateId}
            onChange={(e) => setShipDateId(e.target.value)}
            className="ml-3 px-4 py-1.5 border border-gray-400 rounded-md"
          >
            <option value="">-- เลือกรอบเรือ --</option>
            {shipDates
              .filter((d) => ['ODA J4410', 'D2U'].includes(d.warehouseReceivingAddress?.addressNickName))
              .map((d) => (
                <option key={d.id} value={d.id}>
                  {formatDate(d.shipDate)}
                </option>
              ))}
          </select>
          <label className="flex items-center gap-2 ml-auto text-sm text-gray-600 cursor-pointer select-none" title="กันสแกนโดนบาร์โค้ด tracking ข้างๆ QR">
            <input type="checkbox" checked={smileOnly} onChange={(e) => setSmileOnly(e.target.checked)} />
            รับเฉพาะ QR SMILE
          </label>
        </div>

        {/* Barcode input */}
        <div className="relative mb-2">
          <input
            ref={inputRef}
            type="text"
            value={trackingInput}
            onChange={(e) => setTrackingInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="สแกนบาร์โค้ด หรือพิมพ์ Tracking JP แล้วกด Enter..."
            className="w-full px-4 py-4 text-lg border-2 border-indigo-400 rounded-xl focus:outline-none focus:border-indigo-600 shadow-sm pr-16"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
          {isLoading && (
            <div className="absolute right-4 top-1/2 -translate-y-1/2">
              <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
        <label className="flex items-center gap-2 mb-2 text-sm text-gray-600 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={scanOnly}
            onChange={(e) => setScanOnly(e.target.checked)}
            className="w-4 h-4 accent-indigo-600"
          />
          สแกนอย่างเดียว (ไม่บันทึก ไม่พิมพ์)
        </label>
        <label className="flex items-center gap-2 mb-2 text-sm text-gray-600 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={bypassDuplicate}
            onChange={(e) => setBypassDuplicate(e.target.checked)}
            className="w-4 h-4 accent-indigo-600"
          />
          พิมพ์ซ้ำได้ (ข้ามการบล็อกพัสดุซ้ำ)
        </label>
        <label className="flex items-center gap-2 mb-6 text-sm text-gray-600 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={bypassNotFound}
            onChange={(e) => setBypassNotFound(e.target.checked)}
            className="w-4 h-4 accent-indigo-600"
          />
          พิมพ์แม้ไม่พบข้อมูล (สร้างกล่องพร้อมเลขแทร็ก)
        </label>

        {/* Generate label (no barcode) */}
        <div className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
          <div className="flex items-center gap-3 mb-3">
            <button
              onClick={() => setFindModalOpen(true)}
              className="px-4 py-2 bg-white border border-gray-300 hover:border-indigo-400 text-gray-700 text-sm font-medium rounded-lg transition-colors"
            >
              Find Order
            </button>
            <button
              onClick={handleGenerate}
              className={`px-5 py-2 text-white text-sm font-medium rounded-lg transition-colors ${
                selectedOrder ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-500 hover:bg-amber-600'
              }`}
            >
              {selectedOrder ? 'Generate Label' : 'พักไว้ (ON-HOLD)'}
            </button>
            <button
              onClick={() => setHeldModalOpen(true)}
              className="px-4 py-2 bg-white border border-amber-300 hover:border-amber-500 text-amber-700 text-sm font-medium rounded-lg transition-colors"
            >
              ของที่พักไว้
            </button>
            <span className="text-xs text-gray-400">Box #{boxNo}</span>
          </div>
          {selectedOrder ? (
            <div className="flex items-center gap-3 text-sm">
              <span className="font-medium text-gray-800">{selectedOrder.smileNameInput || '-'}</span>
              <span className="text-gray-400">#{selectedOrder.id}</span>
              {selectedOrder.url && selectedOrder.url !== '-' && (
                <span className="text-gray-400 truncate max-w-xs">{selectedOrder.url}</span>
              )}
              <button onClick={() => setSelectedOrder(null)} className="text-xs text-red-400 hover:underline ml-auto">ล้าง</button>
            </div>
          ) : (
            <p className="text-xs text-gray-400">ยังไม่ได้เลือก order — จะพักไว้ (ON-HOLD) และไปโผล่ใน "ของที่พักไว้"</p>
          )}
        </div>

        {findModalOpen && (
          <FindOrderModal
            profiles={profiles}
            profilesReady={profilesReady}
            onSelect={(order) => { setFindModalOpen(false); printForOrder(order); }}
            onClose={() => setFindModalOpen(false)}
          />
        )}

        {heldModalOpen && (
          <HeldItemsModal
            shipDates={shipDates}
            profiles={profiles}
            profilesReady={profilesReady}
            onClose={() => setHeldModalOpen(false)}
          />
        )}

        {/* Duplicate dealerBoxNo — pick the right twin */}
        {chooser.open && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <h2 className="font-semibold text-gray-800">กล่องซ้ำ #{chooser.boxNo} — เลือกอันที่ถูกต้อง</h2>
                <button onClick={() => setChooser((c) => ({ ...c, open: false }))} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
              </div>
              <div className="px-6 py-2 text-xs text-gray-400 border-b border-gray-100 bg-gray-50">
                QR นี้ตรงกับ {chooser.candidates.length} กล่อง — เลือกกล่องที่กำลังสแกนอยู่
              </div>
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
                {chooser.candidates.map((c) => (
                  <button
                    key={c.weeklyShipUuid}
                    onClick={() => handleChooserPick(c)}
                    className="w-full flex items-center gap-3 text-left p-3 rounded-lg border border-gray-200 hover:bg-indigo-50"
                  >
                    {c.shipImageUrl && c.shipImageUrl.startsWith('http') ? (
                      <img src={c.shipImageUrl} alt="" className="w-14 h-14 object-cover rounded border border-gray-200" />
                    ) : (
                      <span className="w-14 h-14 flex items-center justify-center text-gray-300 border border-gray-200 rounded">—</span>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-gray-800 truncate">
                        {c.smileName || c.smileNameInput || (c.state === 'held' ? `พักไว้ ${c.holdTag || ''}` : 'ไม่ระบุชื่อ')}
                      </div>
                      <div className="text-xs text-gray-500 font-mono truncate">{c.trackingJp || '-'}</div>
                      <div className="text-xs text-gray-400">
                        {c.weight != null ? `${c.weight} kg` : '-'} · {c.state === 'matched' ? 'จับคู่แล้ว' : c.state === 'held' ? 'พักไว้' : 'ตกรอบ'}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Result card */}
        {result && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="bg-indigo-600 px-5 py-3 flex items-center justify-between">
              <span className="text-white font-semibold text-sm">ผลลัพธ์</span>
              <span className="text-indigo-200 text-xs font-mono truncate max-w-xs">{result.trackingJp || ''}</span>
            </div>
            <div className="p-5 grid grid-cols-2 gap-4">
              <InfoRow label="Contact" value={contact} highlight />
              <InfoRow label="Shop" value={shop} highlight />
              <InfoRow label="Smile Name Input" value={smileNameInput} highlight />
              <InfoRow label="Smile Name" value={smileName} />
              <InfoRow label="น้ำหนักเรียกเก็บ" value={billableWeight != null ? `${billableWeight} kg` : 'ไม่ได้เชื่อมตาชั่ง'} highlight />
              <InfoRow label="Amount" value={amount !== '-' ? `¥${Number(amount).toLocaleString()}` : '-'} />
              <InfoRow label="Qty" value={qty} />
              <div className="col-span-2">
                <InfoRow label="URL" value={url} isUrl />
              </div>
            </div>
          </div>
        )}

        {/* Not found */}
        {notFound && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-center">
            <p className="text-red-600 font-semibold text-lg">ไม่พบข้อมูล</p>
            <p className="text-red-400 text-sm mt-1">ลองสแกนใหม่อีกครั้ง</p>
          </div>
        )}

        {/* Idle hint */}
        {!result && !notFound && !isLoading && (
          <div className="text-center text-gray-300 mt-12">
            <svg className="w-16 h-16 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v1m0 14v1M4.22 4.22l.7.7m12.16 12.16.7.7M1 12h1m18 0h1M4.22 19.78l.7-.7M18.36 5.64l.7-.7" />
            </svg>
            <p className="text-sm">พร้อมรับการสแกน</p>
          </div>
        )}

        {/* Scan history */}
        {history.length > 0 && (
          <div className="mt-8">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">ประวัติการสแกน</h2>
            <div className="rounded-xl border border-gray-100 overflow-x-auto shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-400 text-xs uppercase">
                    <th className="text-left px-4 py-2 font-medium">#</th>
                    <th className="text-left px-4 py-2 font-medium">Smile Name Input</th>
                    <th className="text-left px-4 py-2 font-medium">Shop</th>
                    <th className="text-left px-4 py-2 font-medium">Amount</th>
                    <th className="text-left px-4 py-2 font-medium">Tracking JP</th>
                    <th className="text-left px-4 py-2 font-medium">Box</th>
                    <th className="text-left px-4 py-2 font-medium">เวลา</th>
                    <th className="px-4 py-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((row, i) => (
                    <tr key={row.id || i} className={`border-t border-gray-50 ${!row.found ? 'bg-red-50' : 'bg-white'}`}>
                      <td className="px-4 py-2 text-gray-400">{history.length - i}</td>
                      <td className="px-4 py-2 font-medium text-gray-800">{row.smileNameInput || '-'}</td>
                      <td className="px-4 py-2 text-gray-600">{row.shop || '-'}</td>
                      <td className="px-4 py-2 text-gray-600">
                        {row.amount != null ? `¥${Number(row.amount).toLocaleString()}` : '-'}
                      </td>
                      <td className="px-4 py-2 font-mono text-gray-600">{row.trackingJp}</td>
                      <td className="px-4 py-2 font-mono text-gray-700">
                        {row.matchedBoxNo || '-'}
                        {row.autoMatched && (
                          <span className="ml-2 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-xs font-medium">auto ✓</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-gray-400 whitespace-nowrap">
                        {new Date(row.createdAt).toLocaleTimeString('th-TH')}
                      </td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        {row.weeklyShipUuid && (
                          <button
                            onClick={() => handleCancelBox(row)}
                            className="text-xs text-red-500 hover:underline"
                          >
                            ยกเลิก
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

// ── Scale status widget ───────────────────────────────────────────────────────

const ScaleStatus = ({ status, weight, onConnect, onDisconnect }) => {
  const connected = status === 'connected';
  return (
    <div className="flex flex-col items-end gap-2 shrink-0">
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${connected ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
        <span className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
        {connected ? (weight != null ? `${weight.toFixed(3)} kg` : 'รอข้อมูล…') : 'ตาชั่งไม่ได้เชื่อมต่อ'}
      </div>
      {!connected
        ? <button onClick={onConnect}    className="text-xs text-indigo-600 hover:underline">เชื่อมต่อตาชั่ง</button>
        : <button onClick={onDisconnect} className="text-xs text-red-400 hover:underline">ตัดการเชื่อมต่อ</button>
      }
    </div>
  );
};

// ── InfoRow ───────────────────────────────────────────────────────────────────

const InfoRow = ({ label, value, highlight, isUrl }) => (
  <div>
    <p className="text-xs text-gray-400 mb-0.5">{label}</p>
    {isUrl ? (
      value !== '-' ? (
        <a href={value} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm break-all">
          {value}
        </a>
      ) : (
        <p className="text-gray-400 text-sm">-</p>
      )
    ) : (
      <p className={`text-base font-medium break-all ${highlight ? 'text-gray-900' : 'text-gray-600'}`}>
        {value}
      </p>
    )}
  </div>
);

export default TrackingLookup;
